import React from 'react'
import { useDocumentLibrary } from 'common/hooks/useDocumentLibrary'
import { useEffect, useRef, useState } from 'react'
import { useConsent } from 'hooks/useConsent'
import { useLawFinder } from 'common/hooks/useLawFinder'
import { useLocation, useNavigate } from 'react-router-dom'
import Ratings from 'cwp-components/utils/Ratings'
import PrivacyConsentCheckbox from 'cwp-components/PrivacyConsentCheckbox'
import { ArrowDown } from './icons/ArrowDown'
import { DocumentLibraryButton } from './DocumentLibraryButton'
import { LawSearchButton } from './LawSearchButton'
import { InfoText } from './InfoText'
import { InteractiveInput } from './InteractiveInput'

const Documents = () => {
  const commonCardStyles = 'mb-4 p-[20px] lg:w-[716px] 2xl:w-[900px] 3xl:w-[1240px] lg:p-10  2xl:px-10 2xl:py-20 3xl:p-20 bg-white rounded-3xl shadow-[0px_4px_20px_0px_rgba(0,26,61,0.12)]'
  const navigate = useNavigate()
  const [docRating, setDocRating] = useState(0)
  const [feedbackText, setFeedbackText] = useState('')
  const {
    updateDocumentLibrarySearchValue,
    search,
    documentLibrary,
    loadDocumentLibrary,
    isFeedbackProcessing,
    submitFeedBack,
  } = useDocumentLibrary()
  const {
    lawFinderSearch,
    lawFinder,
    loadLawFinder,
    updateLawFinderSearchValue,
    getExampleContents,
  } = useLawFinder()

  useEffect(loadDocumentLibrary, [loadDocumentLibrary])
  useEffect(() => {
    const fetchLawFinder = async () => {
      await loadLawFinder()
    }

    fetchLawFinder()
  }, [loadLawFinder])
  useEffect(getExampleContents, [getExampleContents])

  const documentDictionaryRef = useRef<HTMLDivElement>(null)
  const lawFinderRef = useRef<HTMLDivElement>(null)
  const scrollableRefs = { documentDictionaryRef, lawFinderRef }
  const location = useLocation()
  const sectionId = location?.state?.scrollTo
  const expandedSectionId = location?.state?.section
  const [hasScrolled, setHasScrolled] = useState(false)
  const [consent, setConsent] = useConsent()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [expandedSection, setExpandedSection] = useState('lawFinder')

  const toggleExpanded = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }
  useEffect(() => {
    if (expandedSectionId) setExpandedSection(expandedSectionId)
  }, [expandedSectionId])
  useEffect(() => {
    if (sectionId && scrollableRefs[sectionId]?.current) {
      scrollableRefs[sectionId].current.scrollIntoView({ behavior: 'smooth' })
      setHasScrolled(true)
    }
  }, [sectionId, scrollableRefs, hasScrolled])

  useEffect(() => {
    if ((search || lawFinderSearch) && consent !== 'using') {
      setConsent('using')
    }
  }, [search, lawFinderSearch, consent])

  const handleSubmit = async () => {
    try {
      await submitFeedBack({
        source: 'document_dictionary',
        rating: docRating,
        feedback: feedbackText,
      })
      setDocRating(0)
      setFeedbackText('')
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <div
      id='documents'
      className='flex flex-col sm:flex-row overflow-x-hidden px-4 !mb-24 mt-[150px] 2xl:mt-[184px] 3xl:mt-[284px]'
    >
      <div
        className={`hidden sm:flex flex-col transform -translate-x-1/2 ${expandedSection !== 'documentDictionary' ? 'invisible' : ''
          }`}
      >
        <LawSearchButton onClick={() => setExpandedSection('lawFinder')} />
      </div>
      <div ref={documentDictionaryRef}
        className={`${commonCardStyles} mt-3 ${expandedSection !== 'documentDictionary' ? 'sm:hidden' : ''
          }`}
      >
        <div className='relative mx-auto'>
          <div className='text-center'>
            <InfoText
              heading='Document Dictionary'
              description='Type in the name of a legal document to get its purpose.'
            />
            <InteractiveInput
              label="Legal document's name"
              value={search}
              disabled={consent === 'ask'}
              setValue={updateDocumentLibrarySearchValue}
            />
            {consent !== 'using' && <PrivacyConsentCheckbox />}
          </div>
        </div>
        {documentLibrary.length > 0 && (
          <>
            {documentLibrary.map((doc) => {
              const isExpanded = expandedId === doc.document_id
              return (
                <div key={doc.document_id} className='w-full space-y-4  py-6 '>
                  <div
                    data-document-link='true'
                    data-property-1='Default'
                    className='w-full rounded-lg inline-flex justify-between items-center cursor-pointer'
                    onClick={() => toggleExpanded(doc.document_id)}
                  >
                    <div className='flex-1 inline-flex flex-col justify-center items-start gap-2.5'>
                      <div className="text-gray-900 sm:text-lg text-base font-bold leading-normal tracking-wide font-['DM_Sans']">
                        {doc.predicted_document}
                      </div>
                    </div>
                    <ArrowDown />
                  </div>
                  {isExpanded && (
                    <div className='sm:text-sm text-xs'>
                      <p className='mb-4'>{doc.description}</p>
                      <p className='text-[#25395C] font-bold'>
                        View document:{' '}
                        <span
                          className='text-[#101828] cursor-pointer'
                          onClick={() =>
                            navigate(`/legal-documents/${doc.document_id}`)
                          }
                        >
                          {doc.predicted_document}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              )
            })}

            <div className='flex sm:justify-end justify-center md:mt-[60px] mt-[20px]'>
              <div className=''>
                <h1 className='!text-sm sm:!text-lg whitespace-nowrap text-center md:mb-[24px] mb-[16px] text-blue-950 font-thin'>
                  How helpful was this document?
                </h1>
                <Ratings
                  rating={docRating}
                  setRating={setDocRating}
                  feedback={true}
                  setFeedbackText={(value: string) => setFeedbackText(value)}
                  label={'helpful'}
                  onSubmit={handleSubmit}
                  isProcessing={isFeedbackProcessing}
                />
              </div>
            </div>
          </>
        )}
      </div>
      <div ref={lawFinderRef}
        className={`${commonCardStyles} ${expandedSection !== 'lawFinder' && 'sm:hidden'
          }`}
      >
        <div className='relative mx-auto'>
          <div className='text-center'>
            <InfoText
              heading='Law Finder'
              description='Type in an explanation to get help finding the correct area of
                  law.'
            />
            <InteractiveInput
              label='Type in your need'
              value={lawFinderSearch}
              disabled={consent === 'ask'}
              setValue={updateLawFinderSearchValue}
            />
            {consent !== 'using' && <PrivacyConsentCheckbox />}
          </div>
        </div>
        {lawFinder.length > 0 && (
          <>
            {lawFinder.map((area) => {
              const isExpanded = expandedId === area.id

              return (
                <div key={area.id} className='w-full space-y-4 py-6 '>
                  <div
                    data-document-link='true'
                    data-property-1='Default'
                    className='w-full self-stretch rounded-lg inline-flex justify-between items-center cursor-pointer'
                    onClick={() => toggleExpanded(area.id)}
                  >
                    <div className='flex-1 inline-flex flex-col justify-center items-start gap-2.5'>
                      <div className="self-stretch text-gray-900 text-lg font-bold leading-normal tracking-wide font-['DM_Sans']">
                        {area.master_term}
                      </div>
                    </div>
                    <ArrowDown />
                  </div>
                  {isExpanded && (
                    <div className='text-sm'>
                      <p className='mb-4'>{area.text_definition}</p>
                    </div>
                  )}
                </div>
              )
            })}
          </>
        )}
      </div>
      <div
        className={`hidden sm:flex flex-col transform translate-x-1/2 ${expandedSection !== 'lawFinder' && 'invisible'
          }`}
      >
        <DocumentLibraryButton onClick={() => setExpandedSection('documentDictionary')} />
      </div>
    </div>
  )
}

export default Documents
